import { useEffect, useRef, useState } from 'react'
import { useSearch } from '../../contexts/SearchContext'
import { googleMapsApiKey } from '../../config'

const AdminMapPage = () => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null)
  const [position, setPosition] = useState<{ lat: number; lng: number } | null>(null)
  const mapRef = useRef<any>(null)
  const markerRef = useRef<any>(null)
  const rectangleRef = useRef<any>(null)
  const isDrawingRef = useRef<boolean>(false)
  const startLatLngRef = useRef<any>(null)
  const listenersRef = useRef<any[]>([])
  const domListenersRef = useRef<(() => void)[]>([])
  const [boundsInfo, setBoundsInfo] = useState<{
    ne: { lat: number; lng: number }
    sw: { lat: number; lng: number }
  } | null>(null)
  const [pointInfo, setPointInfo] = useState<{ lat: number; lng: number } | null>(null)
  const movedRef = useRef<boolean>(false)
  const { searchValue } = useSearch()
  const isMetaDownRef = useRef<boolean>(false)

  // Auto-copy bounds JSON to clipboard whenever bounds change
  useEffect(() => {
    if (!boundsInfo) return
    const payload = {
      southwestLatitude: boundsInfo.sw.lat.toFixed(6),
      southwestLongitude: boundsInfo.sw.lng.toFixed(6),
      northeastLatitude: boundsInfo.ne.lat.toFixed(6),
      northeastLongitude: boundsInfo.ne.lng.toFixed(6),
    }
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2)).catch(() => {})
  }, [boundsInfo])

  // Auto-copy point JSON to clipboard whenever point changes
  useEffect(() => {
    if (!pointInfo) return
    const payload = {
      latitude: pointInfo.lat.toFixed(6),
      longitude: pointInfo.lng.toFixed(6),
    }
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2)).catch(() => {})
  }, [pointInfo])

  // Recenter when search bar contains obvious lat,lng
  useEffect(() => {
    if (!mapRef.current || !searchValue) return
    // Match patterns like "41.881,-87.623" or "41.881 -87.623"
    const match = searchValue
      .trim()
      .match(/^\s*(-?\d{1,3}(?:\.\d+)?)\s*[ ,]+\s*(-?\d{1,3}(?:\.\d+)?)\s*$/)
    if (!match) return
    const lat = parseFloat(match[1])
    const lng = parseFloat(match[2])
    if (Number.isNaN(lat) || Number.isNaN(lng)) return
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return
    const pos = { lat, lng }
    mapRef.current.setCenter(pos)
    setBoundsInfo(null)
    setPointInfo(pos)
    if (markerRef.current) {
      markerRef.current.setPosition(pos)
      markerRef.current.setTitle('Search coordinates')
    }
  }, [searchValue])

  // On Enter in search bar: text search via Places and recenter
  useEffect(() => {
    const handleEnter = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return
      if (!mapRef.current || !searchValue || !window.google?.maps) return
      // If it's coordinates, skip (handled above on change)
      const coordMatch = searchValue
        .trim()
        .match(/^\s*(-?\d{1,3}(?:\.\d+)?)\s*[ ,]+\s*(-?\d{1,3}(?:\.\d+)?)\s*$/)
      if (coordMatch) return
      const places = (window.google as any)?.maps?.places
      if (!places) return
      // Prefer new Places API (Place + AutocompleteService) for text search
      if (places.Place && places.AutocompleteService) {
        const ac = new places.AutocompleteService()
        ac.getPlacePredictions({ input: searchValue }, (preds: any[], status: any) => {
          const ok = (places.PlacesServiceStatus && places.PlacesServiceStatus.OK) || 'OK'
          if (status !== ok || !preds || !preds[0]?.place_id) return
          const place = new places.Place({ id: preds[0].place_id })
          place
            .fetchFields({ fields: ['location'] })
            .then(() => {
              const loc = place.location
              if (loc) {
                const lat =
                  typeof (loc as any).lat === 'function' ? (loc as any).lat() : (loc as any).lat
                const lng =
                  typeof (loc as any).lng === 'function' ? (loc as any).lng() : (loc as any).lng
                mapRef.current.setCenter(loc)
                setBoundsInfo(null)
                if (typeof lat === 'number' && typeof lng === 'number') {
                  const pos = { lat, lng }
                  setPointInfo(pos)
                  if (markerRef.current) {
                    markerRef.current.setPosition(loc as any)
                    markerRef.current.setTitle('Search result')
                  }
                }
              }
            })
            .catch(() => {})
        })
        return
      }
      // Fallback: if legacy PlacesService is available, try textSearch
      if (places.PlacesService) {
        const service = new places.PlacesService(mapRef.current)
        service.textSearch({ query: searchValue }, (results: any, status: any) => {
          const ok = places.PlacesServiceStatus.OK
          if (status === ok && results && results[0]?.geometry?.location) {
            const loc = results[0].geometry.location
            const lat = loc.lat?.()
            const lng = loc.lng?.()
            mapRef.current.setCenter(loc)
            setBoundsInfo(null)
            if (typeof lat === 'number' && typeof lng === 'number') {
              const pos = { lat, lng }
              setPointInfo(pos)
              if (markerRef.current) {
                markerRef.current.setPosition(loc)
                markerRef.current.setTitle(results[0]?.name || 'Search result')
              }
            }
          }
        })
      }
    }

    window.addEventListener('keydown', handleEnter)
    return () => window.removeEventListener('keydown', handleEnter)
  }, [searchValue])

  // Get current position
  useEffect(() => {
    if (!navigator.geolocation) {
      setPosition({ lat: 39.8283, lng: -98.5795 }) // Fallback: center of US
      return
    }

    navigator.geolocation.getCurrentPosition(
      pos => {
        setPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude })
      },
      () => setPosition({ lat: 39.8283, lng: -98.5795 }),
      { enableHighAccuracy: true, timeout: 10000 }
    )
  }, [])

  // Load Google Maps and render
  useEffect(() => {
    if (!position || !mapContainerRef.current) return

    const initMap = () => {
      const map = new window.google.maps.Map(mapContainerRef.current as HTMLElement, {
        center: position,
        zoom: 15,
        mapTypeId: 'roadmap',
        mapTypeControl: false,
        streetViewControl: false,
      })

      markerRef.current = new window.google.maps.Marker({ position, map, title: 'You are here' })

      mapRef.current = map

      // Setup click-drag rectangle drawing (only when Command/Meta is held)
      const onMouseDown = (e: any) => {
        const metaActive = Boolean(e?.domEvent?.metaKey) || isMetaDownRef.current
        if (!metaActive) {
          // Allow normal map panning
          return
        }

        isDrawingRef.current = true
        startLatLngRef.current = e.latLng
        movedRef.current = false
        map.setOptions({ draggable: false })

        if (rectangleRef.current) {
          rectangleRef.current.setMap(null)
          rectangleRef.current = null
        }

        rectangleRef.current = new window.google.maps.Rectangle({
          bounds: new window.google.maps.LatLngBounds(e.latLng, e.latLng),
          strokeColor: '#5A8A7C',
          strokeOpacity: 0.9,
          strokeWeight: 2,
          fillColor: '#5A8A7C',
          fillOpacity: 0.2,
          map,
        })
      }

      const onMouseMove = (e: any) => {
        if (!isDrawingRef.current || !rectangleRef.current || !startLatLngRef.current) return
        const start = startLatLngRef.current
        const current = e.latLng

        // Consider as drag if moved beyond small threshold (~11m)
        const latDiff = Math.abs(start.lat() - current.lat())
        const lngDiff = Math.abs(start.lng() - current.lng())
        if (latDiff > 0.0001 || lngDiff > 0.0001) {
          movedRef.current = true
        }

        const north = Math.max(start.lat(), current.lat())
        const south = Math.min(start.lat(), current.lat())
        const east = Math.max(start.lng(), current.lng())
        const west = Math.min(start.lng(), current.lng())

        const bounds = new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(south, west),
          new window.google.maps.LatLng(north, east)
        )
        rectangleRef.current.setBounds(bounds)

        const ne = bounds.getNorthEast()
        const sw = bounds.getSouthWest()
        setBoundsInfo({
          ne: { lat: ne.lat(), lng: ne.lng() },
          sw: { lat: sw.lat(), lng: sw.lng() },
        })
        setPointInfo(null)
      }

      const stopDrawing = (e?: any) => {
        if (!isDrawingRef.current) return
        isDrawingRef.current = false
        startLatLngRef.current = null
        map.setOptions({ draggable: true })

        if (movedRef.current) {
          if (rectangleRef.current) {
            const b = rectangleRef.current.getBounds()
            if (b) {
              const ne = b.getNorthEast()
              const sw = b.getSouthWest()
              setBoundsInfo({
                ne: { lat: ne.lat(), lng: ne.lng() },
                sw: { lat: sw.lat(), lng: sw.lng() },
              })
            }
          }
        } else {
          // Single click selection: clear rectangle and set point
          if (rectangleRef.current) {
            rectangleRef.current.setMap(null)
            rectangleRef.current = null
          }
          setBoundsInfo(null)
          const latLng = e?.latLng || map.getCenter()
          setPointInfo({ lat: latLng.lat(), lng: latLng.lng() })
        }
      }

      listenersRef.current.push(
        map.addListener('mousedown', onMouseDown),
        map.addListener('mousemove', onMouseMove),
        map.addListener('mouseup', (e: any) => stopDrawing(e)),
        map.addListener('dragstart', stopDrawing)
      )

      // Also stop drawing if mouseup occurs outside the map canvas
      const onWindowMouseUp = () => stopDrawing()
      window.addEventListener('mouseup', onWindowMouseUp)
      domListenersRef.current.push(() => window.removeEventListener('mouseup', onWindowMouseUp))
    }

    if (window.google?.maps) {
      initMap()
      return
    }

    const scriptId = 'google-maps-sdk'
    let script = document.getElementById(scriptId) as HTMLScriptElement | null
    const onLoad = () => initMap()

    if (!script) {
      script = document.createElement('script')
      script.id = scriptId
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places`
      script.async = true
      script.defer = true
      script.onload = onLoad
      document.head.appendChild(script)
    } else if (window.google?.maps) {
      initMap()
    } else {
      script.addEventListener('load', onLoad, { once: true })
    }

    return () => {
      if (script) {
        script.onload = null
      }
      // Cleanup listeners
      if (listenersRef.current.length > 0) {
        listenersRef.current.forEach(l => l.remove && l.remove())
        listenersRef.current = []
      }
      if (domListenersRef.current.length > 0) {
        domListenersRef.current.forEach(remove => remove())
        domListenersRef.current = []
      }
    }
  }, [position])

  // Track Meta (Command) key state globally so we can start drawing only when held
  useEffect(() => {
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Meta') {
        isMetaDownRef.current = true
      }
    }
    const onKeyUp = (ev: KeyboardEvent) => {
      if (ev.key === 'Meta') {
        isMetaDownRef.current = false
      }
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
    }
  }, [])

  return (
    <div className="map-container">
      <div ref={mapContainerRef} className="map-container-inner" />
      {boundsInfo && (
        <div
          style={{
            position: 'absolute',
            left: 10,
            bottom: 10,
            background: 'rgba(0,0,0,0.6)',
            color: '#fff',
            padding: '8px 10px',
            borderRadius: 4,
            fontSize: 12,
            lineHeight: 1.4,
            zIndex: 3,
          }}
        >
          <div>
            NE: {boundsInfo.ne.lat.toFixed(6)}, {boundsInfo.ne.lng.toFixed(6)}
          </div>
          <div>
            SW: {boundsInfo.sw.lat.toFixed(6)}, {boundsInfo.sw.lng.toFixed(6)}
          </div>
        </div>
      )}
      {!boundsInfo && pointInfo && (
        <div
          style={{
            position: 'absolute',
            left: 10,
            bottom: 10,
            background: 'rgba(0,0,0,0.6)',
            color: '#fff',
            padding: '8px 10px',
            borderRadius: 4,
            fontSize: 12,
            lineHeight: 1.4,
            zIndex: 3,
          }}
        >
          <div>Lat: {pointInfo.lat.toFixed(6)}</div>
          <div>Lng: {pointInfo.lng.toFixed(6)}</div>
        </div>
      )}
    </div>
  )
}

export default AdminMapPage
